/**
 * product-metadata-grouping.ts — product_metadata 行 → 逻辑产品分组塑形（支付产品重设计视图地基）
 *
 * 把 per-通道的 ProductMetadataRow[] 按 access_level_id 归组成「逻辑产品」（#387 §2 hybrid 模型）：
 *  - 一个逻辑产品 = 一个 access_level_id，其下按支付通道各一行（权益可不同）。
 *  - access_level_id 为空的行 = 未归组、不进 C 端 serving → 单列 unassigned。
 *
 * **通道模型 = 结算通道闭集派生**：payment_method 取 `SETTLEMENT_CHANNELS`（IAP / STRIPE / 已登记框架通道），
 * App 单平台（apps.platform）→ **IAP 内购商店名由 platform 派生**（iOS→App Store / Android→Google Play /
 * 其它→通用「内购」，不臆断商店）。
 *  - 齐备度按 shared `channelRowGapsOf`（`03-数据字典/70-产品配置.md` §2）：一条 IAP 行 + 至少一条非 IAP 行；
 *    STRIPE 恒计入，框架通道行只在 CONSUMABLE 时计入。不要求为本 App 不用的通道建行。
 *
 * **效果维度（dd614 / adm #400）**：组级另派生 `effect`（效果 / 产品位 `tier_id` / 时长 / 跨通道一致性 /
 * 改时长 gate）；`computeReadingPassCoverage` 按每个已出现的 `tier_id` 报是否有 IAP + 任一参与非 IAP 行。
 */

import {
  type ChannelRowGap,
  type ConsumableEffect,
  NON_BLOCKING_VALIDATION_ISSUES,
  type ReadingPassTierId,
  SETTLEMENT_CHANNELS,
  type SettlementChannel,
  channelRowGapsOf,
  isFrameworkSettlementChannel,
  isIapPairedSettlementChannel,
} from '@novel-admin/shared';
import {
  type ProductMetadataRow,
  readingPassDurationSecondsOf,
  readingPassTierIdOf,
} from './product-metadata-adapter';

/** 组内通道行与筛选项的展示次序（= 结算通道闭集次序：IAP → STRIPE → 已登记框架通道）*/
export const CHANNEL_DISPLAY_ORDER: readonly SettlementChannel[] = SETTLEMENT_CHANNELS;

/**
 * IAP 内购商店名由 App 平台派生（App 单平台）：iOS→App Store / Android→Google Play。
 * 未配置 / undefined（仅 synthetic / unknown App 回退）→ 通用「内购」，不臆断具体商店。
 * platform 取自 AppRow adapter 映射的 canonical apps.platform。
 */
export function deriveIapStoreLabel(platform: string | undefined): string {
  if (platform?.includes('iOS')) return 'App Store';
  if (platform?.includes('Android')) return 'Google Play';
  return '内购';
}

/** 三方通道展示名（框架通道取正式名，与设置页 / 策略面板同口径）*/
const THIRD_PARTY_CHANNEL_LABEL: Record<Exclude<SettlementChannel, 'IAP'>, string> = {
  STRIPE: '三方 (Stripe)',
  PAYERMAX: 'PayerMax',
};

/** 通道展示名：三方通道取正式名；IAP→派生商店名 */
export function channelStoreLabel(
  paymentMethod: SettlementChannel,
  platform: string | undefined,
): string {
  return paymentMethod === 'IAP'
    ? deriveIapStoreLabel(platform)
    : (THIRD_PARTY_CHANNEL_LABEL[paymentMethod] ?? paymentMethod);
}

/** 通道短名：三方通道去掉「三方」前缀（Stripe / PayerMax），IAP→派生商店名；用于「A + B」并列与「补通道 · X」*/
export function channelShortLabel(
  paymentMethod: SettlementChannel,
  platform: string | undefined,
): string {
  return paymentMethod === 'STRIPE' ? 'Stripe' : channelStoreLabel(paymentMethod, platform);
}

/** 齐备缺口文案：缺 IAP 行 →「缺 App Store」；缺非 IAP 行 →「缺非 IAP 通道」（不点名某个三方通道）*/
export function channelRowGapLabel(gap: ChannelRowGap, platform: string | undefined): string {
  return gap === 'IAP' ? `缺 ${deriveIapStoreLabel(platform)}` : '缺非 IAP 通道';
}

/** 可作为 IAP 配对参与行的三方通道短名并列（「Stripe 或 PayerMax」）*/
export function pairedChannelNames(platform: string | undefined): string {
  return CHANNEL_DISPLAY_ORDER.filter(isIapPairedSettlementChannel)
    .map((ch) => channelShortLabel(ch, platform))
    .join(' 或 ');
}

/** 通道归类（内购 IAP / 三方 WEB）——子行按大类分组展示 */
export type ChannelCategory = 'IAP' | 'WEB';
export function channelCategory(paymentMethod: SettlementChannel): ChannelCategory {
  return paymentMethod === 'IAP' ? 'IAP' : 'WEB';
}

/**
 * 该逻辑产品还能补哪个通道：已有行的通道不能再建（每 product 每通道一行），框架通道只售一次性商品
 * （订阅商品返回 `disabledReason`，入口置灰并说明原因，不静默隐藏）。
 */
export interface AddableChannel {
  channel: SettlementChannel;
  disabledReason: string | null;
}
export function addableChannelsOf(
  channels: readonly ProductMetadataRow[],
  productType: ProductMetadataRow['productType'],
): AddableChannel[] {
  const present = new Set(channels.map((c) => c.paymentMethod));
  return CHANNEL_DISPLAY_ORDER.filter((ch) => !present.has(ch)).map((channel) => ({
    channel,
    disabledReason:
      isFrameworkSettlementChannel(channel) && productType !== 'CONSUMABLE'
        ? `订阅商品不支持 ${channelShortLabel(channel, undefined)}（只售一次性商品）`
        : null,
  }));
}

/**
 * 齐备度：缺口按 shared `channelRowGapsOf`（按行判，不论启停）；inactive 行不履约（#387 硬规则①③），
 * 故另按**在架行**再判一次 —— 停用后仍齐备（如 IAP + STRIPE 在架、新建的 PayerMax 行未上架）不算未齐。
 */
export interface Readiness {
  /** `warn` = 有缺口，或在架行凑不齐（停用导致未齐）；可选通道停用而在架行仍齐备时为 `ok` */
  level: 'ok' | 'warn';
  /** 齐备缺口：缺 IAP 行 / 缺可计入的非 IAP 行 */
  gaps: ChannelRowGap[];
  /** 只看在架行时的缺口（`gaps` 为空但它非空 = 停用导致未齐）*/
  activeGaps: ChannelRowGap[];
  /** 有行但已停用的通道 */
  disabled: SettlementChannel[];
  /** active 通道行数 */
  activeCount: number;
  /** 通道行数 */
  total: number;
}

/** 权益字段（各通道可不同）——跨通道发散高亮所用的 key 集 */
const DIVERGENT_KEYS = [
  'coinAmount',
  'bonusAmount',
  'bonusRatio',
  'bonusExpireDays',
] as const satisfies readonly (keyof ProductMetadataRow)[];
export type DivergentKey = (typeof DIVERGENT_KEYS)[number];
/** 逐权益字段是否跨通道发散 + `any` 汇总（ChannelRow 逐单元格高亮 + 分组头「不一致」徽章共用）*/
export type BenefitDivergence = Record<DivergentKey, boolean> & { any: boolean };

/** 可售汇总（#465 读时派生，异于运营开关 isActive）：任一 active 通道不可售 → 发布 gate 提示 */
export interface ServeabilitySummary {
  /** 存在 active 但不可售的通道（首购不进 serving，需修 IAP 基线 / Stripe 权益）*/
  warn: boolean;
  /** active 但不可售的通道集 */
  unserveable: SettlementChannel[];
}

/**
 * 效果汇总（dev-docs `03-数据字典/70-产品配置.md` §2.5）：效果与其参数是 **product 级**字段，
 * 同一 `(app_id, access_level_id)` 的 IAP / STRIPE 两行必须相同；不同即 STRIPE 行 `is_serveable` 不成立。
 */
export interface EffectSummary {
  /** 组内首行的效果（product 级；订阅恒 null）*/
  effect: ConsumableEffect | null;
  /** `READING_PASS_CARD` 的产品位身份；非本效果 / params 不合 shape → null */
  tierId: ReadingPassTierId | null;
  /** `READING_PASS_CARD` 的权益时长（秒）；非本效果 / 非正整数 → null */
  durationSeconds: number | null;
  /** 各通道行的 effect 或 params 不一致（跨通道一致性是 is_serveable 前置，§2.5「跨通道一致性」）*/
  divergent: boolean;
  /**
   * 改 `duration_seconds` 的生命周期 gate（A8 §3 / #773）：配对任一行 `first_published_at` 非空即锁。
   * 两行都是草稿可改；已发布行改时长 → VALIDATION_FAILED，请走原子换代。
   */
  durationLocked: boolean;
  /** 已发布过、挡住改时长的通道 */
  durationBlockingChannels: SettlementChannel[];
}

export interface LogicalProduct {
  accessLevelId: string;
  /** 运营识别名（product 级；取组内首行）*/
  name: string;
  /** 展示标题（解析后）*/
  title: string;
  productType: ProductMetadataRow['productType'];
  subscriptionPeriod: ProductMetadataRow['subscriptionPeriod'];
  /** 该逻辑产品的通道行（每 payment_method 至多一活跃行，#264）*/
  channels: ProductMetadataRow[];
  readiness: Readiness;
  /** 各通道权益（coin/bonus/ratio/expire）逐字段是否跨行不一致（+ any 汇总）*/
  benefitDivergent: BenefitDivergence;
  /** 可售汇总（#465）：active 通道是否有不可售者（is_serveable 读时派生）*/
  serveability: ServeabilitySummary;
  /** 效果汇总（§2.5）：效果 / 产品位 / 时长 + 跨通道一致性 + 改时长 gate */
  effect: EffectSummary;
}

/**
 * 单个卡位的通道配对齐全性（70 §2.5「卡位参与通道」：同一 `tier_id` 一条 IAP 行 + 任一参与的非 IAP 行）。
 * 不要求为本 App 不用的通道建行，故缺口只报「缺 IAP」/「缺非 IAP 参与行」，不点名缺哪个三方通道。
 */
export interface ReadingPassTierCoverage {
  tierId: ReadingPassTierId;
  /** 已配好的参与通道（IAP 在前，其余按结算通道次序）*/
  presentPaymentMethods: SettlementChannel[];
  /** 缺口：缺已配好的 IAP 行 / 缺任一已配好的非 IAP 参与行 */
  gaps: ChannelRowGap[];
  /**
   * 已配好、但当前选不出价格的框架通道参与行（`CHANNEL_PRICE_UNSELECTABLE`）——卡位已配对，
   * 但该通道上仍不可售，App「开启阅读卡」硬闸也会因此拦下。
   */
  unpricedPaymentMethods: SettlementChannel[];
}

/** 全 App 的小时阅读卡配对齐全性汇总 */
export interface ReadingPassCoverage {
  /** App 下存在 `READING_PASS_CARD` 逻辑产品（无则整块不展示）*/
  configured: boolean;
  /** 每个已出现的 tier_id 缺哪个通道 */
  tiers: ReadingPassTierCoverage[];
  /** 任一卡位缺 IAP 或缺非 IAP 参与行 */
  incomplete: boolean;
}

export interface GroupedProductMetadata {
  groups: LogicalProduct[];
  /** access_level_id 为空的行（未归组，不进 C 端 serving）*/
  unassigned: ProductMetadataRow[];
}

function computeReadiness(channels: ProductMetadataRow[]): Readiness {
  const gaps = channelRowGapsOf(channels);
  const activeGaps = channelRowGapsOf(channels.filter((c) => c.isActive));
  const disabled = channels.filter((c) => !c.isActive).map((c) => c.paymentMethod);
  return {
    level: gaps.length || activeGaps.length ? 'warn' : 'ok',
    gaps,
    activeGaps,
    disabled,
    activeCount: channels.filter((c) => c.isActive).length,
    total: channels.length,
  };
}

/** 可售汇总（#465）：active 通道中 is_serveable=false 者（首购不进 serving，需修基线 / 权益）*/
function computeServeability(channels: ProductMetadataRow[]): ServeabilitySummary {
  const unserveable = channels
    .filter((c) => c.isActive && !c.isServeable)
    .map((c) => c.paymentMethod);
  return { warn: unserveable.length > 0, unserveable };
}

/**
 * 「跨通道差值是契约要求的，不是配置分歧」的列 —— 值列 → 该列在 IAP 配对行（STRIPE 与框架通道）上的派生下限列。
 * 契约 `02-API契约/A8-运营支付.md` §1：配对行的 `bonus_amount`（CONSUMABLE 发币与 `COIN_PERIOD_GRANT`
 * 订阅同一公式）下限 = `IAP bonus + ceil(IAP coin × apps.third_party_bonus_ratio)`，**要求高于 IAP**、
 * 且允许再上调（框架通道复用 STRIPE 口径，70 §2）。该列的跨通道差值因此是常态，不是运营配错。
 */
const DERIVED_FLOOR_COLUMN = {
  bonusAmount: 'derivedMinimumBonusAmount',
} as const satisfies Partial<Record<DivergentKey, keyof ProductMetadataRow>>;

/**
 * 该列的发散是否**只是**配对行按契约派生上浮：IAP 行彼此同值（IAP 基线自己散了就是真分歧），
 * 且每个配对行（STRIPE / 框架通道）都 ≥ 自己的派生下限。
 *
 * 派生下限为 `null` 时**不豁免** —— 那是「服务端没给判据」，不是「差值合法」；宁可多报一次也不能
 * 把无判据当通过。数值用 `BigInt` 比 —— 该列是 bigint string（上界 int8），`Number` 会在大额档位丢精度。
 */
function isDerivedUplift(
  channels: ProductMetadataRow[],
  valueKey: keyof typeof DERIVED_FLOOR_COLUMN,
): boolean {
  const floorKey = DERIVED_FLOOR_COLUMN[valueKey];
  const paired = channels.filter((c) => isIapPairedSettlementChannel(c.paymentMethod));
  const others = channels.filter((c) => !isIapPairedSettlementChannel(c.paymentMethod));
  if (paired.length === 0) return false;
  if (new Set(others.map((c) => c[valueKey])).size > 1) return false;
  return paired.every((c) => {
    const floor = c[floorKey];
    if (typeof floor !== 'string' || floor === '') return false;
    try {
      return BigInt(c[valueKey]) >= BigInt(floor);
    } catch {
      return false;
    }
  });
}

/**
 * 逐字段判权益是否跨通道发散（<2 通道恒不发散）；`any` = 任一字段发散。
 *
 * **配对行的契约派生上浮不算发散**（#619 ⑥）：裸等值比对会把每个带赠送比例的充值档、每个带周期 bonus
 * 的订阅都报成「通道权益不一致」，而配对行 bonus 高于 IAP 正是 A8 §1 要求的派生值。警示每条都亮 =
 * 运营学会无视它，真分歧也就跟着被无视了。
 */
function computeBenefitDivergence(channels: ProductMetadataRow[]): BenefitDivergence {
  const d: BenefitDivergence = {
    coinAmount: false,
    bonusAmount: false,
    bonusRatio: false,
    bonusExpireDays: false,
    any: false,
  };
  if (channels.length < 2) return d;
  for (const k of DIVERGENT_KEYS) {
    const raw = new Set(channels.map((c) => c[k])).size > 1;
    // coinAmount 不在豁免列内 —— 契约要求配对行 **等于** IAP，散了就是真分歧
    d[k] =
      raw &&
      !(
        k in DERIVED_FLOOR_COLUMN &&
        isDerivedUplift(channels, k as keyof typeof DERIVED_FLOOR_COLUMN)
      );
  }
  d.any = DIVERGENT_KEYS.some((k) => d[k]);
  return d;
}

/**
 * 效果汇总（§2.5）：效果 / 产品位 / 时长取组内首行（product 级字段，跨通道应同值），
 * 另判跨通道一致性与改时长 gate。
 *
 * 跨通道一致性用 **params 的 JSON 序列化** 比对而非逐 key 手比：契约要求「逐 key 逐值相同」，
 * 而两行的 params 都由同一次逻辑产品级原子更新写入（A8 §3），key 顺序天然一致；不一致必是历史
 * 逐行写留下的失配态，正是要显式暴露的那个态。
 */
function computeEffectSummary(channels: ProductMetadataRow[]): EffectSummary {
  const head = channels[0];
  const effect = head?.consumableEffect ?? null;
  const divergent =
    channels.length > 1 &&
    (new Set(channels.map((c) => c.consumableEffect)).size > 1 ||
      new Set(channels.map((c) => JSON.stringify(c.consumableEffectParams ?? {}))).size > 1);
  const durationBlockingChannels = channels
    .filter((c) => c.firstPublishedAt != null)
    .map((c) => c.paymentMethod);
  return {
    effect,
    tierId: head ? readingPassTierIdOf(head) : null,
    durationSeconds: head ? readingPassDurationSecondsOf(head) : null,
    divergent,
    durationLocked: durationBlockingChannels.length > 0,
    durationBlockingChannels,
  };
}

/**
 * 该产品位在本行上是否**已配好**：`isActive` 且除齐全性外的判据全过。
 *
 * 只差对侧的行必须算「已配」—— 它自己就因缺对侧而 `isServeable=false`，直接读 `isServeable` 是自指，
 * 会把「只配了 `CARD_A`」报成两位都缺，指错运营的修复动作。判据与服务端第一相
 * （`evaluateExceptTierCompleteness`）同构：`NON_BLOCKING_VALIDATION_ISSUES` 不计入。
 */
function isTierSlotConfigured(row: ProductMetadataRow): boolean {
  if (!row.isActive) return false;
  if (row.isServeable) return true;
  return (
    row.validationIssues.length > 0 &&
    row.validationIssues.every((issue) => NON_BLOCKING_VALIDATION_ISSUES.includes(issue))
  );
}

/**
 * 按每个已出现的 `tier_id` 报配对缺口（#773 / 70 §2.5）：一条已配好的 IAP 行 + 任一已配好的非 IAP 参与行
 * （STRIPE 或框架通道）即配对齐全，不要求为本 App 不用的通道建行。
 * 已配好但选不出价格的框架通道参与行单列 `unpricedPaymentMethods`（配对齐全，但该通道上仍不可售）。
 */
export function computeReadingPassCoverage(groups: readonly LogicalProduct[]): ReadingPassCoverage {
  const passGroups = groups.filter((g) => g.effect.effect === 'READING_PASS_CARD');
  if (passGroups.length === 0) {
    return { configured: false, tiers: [], incomplete: false };
  }
  const byTier = new Map<string, ProductMetadataRow[]>();
  for (const g of passGroups) {
    for (const row of g.channels) {
      const tierId = readingPassTierIdOf(row);
      if (tierId === null) continue;
      const list = byTier.get(tierId);
      if (list) list.push(row);
      else byTier.set(tierId, [row]);
    }
  }
  const tiers = [...byTier.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([tierId, rows]) => {
      const configured = rows.filter(isTierSlotConfigured);
      const presentPaymentMethods = CHANNEL_DISPLAY_ORDER.filter((method) =>
        configured.some((r) => r.paymentMethod === method),
      );
      const gaps: ChannelRowGap[] = [];
      if (!presentPaymentMethods.includes('IAP')) gaps.push('IAP');
      if (!presentPaymentMethods.some(isIapPairedSettlementChannel)) gaps.push('NON_IAP');
      const unpricedPaymentMethods = CHANNEL_DISPLAY_ORDER.filter((method) =>
        configured.some(
          (r) =>
            r.paymentMethod === method && r.validationIssues.includes('CHANNEL_PRICE_UNSELECTABLE'),
        ),
      );
      return { tierId, presentPaymentMethods, gaps, unpricedPaymentMethods };
    });
  return {
    configured: true,
    tiers,
    incomplete: tiers.some((t) => t.gaps.length > 0),
  };
}

/**
 * 按 access_level_id 归组 product_metadata 行 → 逻辑产品 + 未归组行。
 * 组内通道行按 CHANNEL_DISPLAY_ORDER 顺序（IAP → STRIPE → 框架通道）排序；组按 access_level_id 字典序。
 */
export function groupProductMetadataByAccessLevel(
  rows: ProductMetadataRow[],
): GroupedProductMetadata {
  const byAccessLevel = new Map<string, ProductMetadataRow[]>();
  const unassigned: ProductMetadataRow[] = [];
  for (const r of rows) {
    if (!r.accessLevelId) {
      unassigned.push(r);
      continue;
    }
    const list = byAccessLevel.get(r.accessLevelId);
    if (list) list.push(r);
    else byAccessLevel.set(r.accessLevelId, [r]);
  }

  // 闭集外取值排在最后（indexOf=-1 会被排到最前，冒充成 IAP 基线行）
  const channelOrder = (m: SettlementChannel) => {
    const idx = CHANNEL_DISPLAY_ORDER.indexOf(m);
    return idx === -1 ? CHANNEL_DISPLAY_ORDER.length : idx;
  };
  const groups: LogicalProduct[] = [...byAccessLevel.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([accessLevelId, chans]) => {
      const channels = [...chans].sort(
        (a, b) =>
          channelOrder(a.paymentMethod) - channelOrder(b.paymentMethod) ||
          a.vendorProductId.localeCompare(b.vendorProductId),
      );
      // product 级字段取组内首行（各通道应同值；不一致由 UI 另行提示/联动，非本 adapter 职责）
      const head = channels[0];
      return {
        accessLevelId,
        name: head?.name ?? '',
        title: head?.title ?? '',
        productType: head?.productType ?? 'CONSUMABLE',
        subscriptionPeriod: head?.subscriptionPeriod ?? null,
        channels,
        readiness: computeReadiness(channels),
        benefitDivergent: computeBenefitDivergence(channels),
        serveability: computeServeability(channels),
        effect: computeEffectSummary(channels),
      };
    });

  return { groups, unassigned };
}
